import type { VoiceGainEvent } from './schedulePlan';

/**
 * Writes a clip's gain envelope onto an `AudioParam`.
 *
 * The mechanical half of the clip envelope: the plan has already decided what
 * the gain should be and when, in seconds after the voice starts, so all that is
 * left is to say it in the terms an `AudioParam` understands. Shared by live
 * playback and the offline renderer, so an export cannot disagree with what was
 * heard.
 *
 * Steps are `setValueAtTime`, ramps are `linearRampToValueAtTime`. Smooth
 * segments arrived already broken into short ramps, because an `AudioParam` has
 * no smoothstep — approximating the curve is the plan's job, and doing it there
 * means it is done identically in both engines and can be tested without one.
 */
export function applyVoiceEnvelope(param: AudioParam, voiceStart: number, events: readonly VoiceGainEvent[]): void {
  if (events.length === 0) return;

  // The first event anchors the param at the level the envelope already holds,
  // so a voice starting mid-clip begins at the right level rather than at
  // whatever the node was built with.
  param.setValueAtTime(events[0].gain, voiceStart + events[0].offsetSec);

  for (let i = 1; i < events.length; i++) {
    const event = events[i];
    const at = voiceStart + event.offsetSec;
    if (event.kind === 'step') param.setValueAtTime(event.gain, at);
    else param.linearRampToValueAtTime(event.gain, at);
  }
}
